import { useCallback, useEffect, useMemo, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { STATUT_TEHNICIAN, eticheta } from "./enums.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

const ADMINISTRARE = ["ADMIN_TENANT", "MANAGER_TEHNIC"];
const OPERATIONAL = [...ADMINISTRARE, "DISPECER", "COORDONATOR"];

const TIPURI_INDISPONIBILITATE = [
  { cod: "Concediu", titlu: "Concediu" },
  { cod: "Instruire", titlu: "Instruire" },
  { cod: "Garda", titlu: "Gardă" },
  { cod: "Indisponibil", titlu: "Indisponibil" },
];

const SUBTABURI = [
  { cod: "oameni", titlu: "Tehnicieni" },
  { cod: "echipe", titlu: "Echipe" },
  { cod: "alocari", titlu: "Alocare pe client" },
];

const azi = () => new Date().toISOString().slice(0, 10);

export default function Echipe({ eu }) {
  const [subtab, setSubtab] = useState("oameni");
  const [eroare, setEroare] = useState("");
  const [expira, setExpira] = useState([]);

  const poateAdministra = ADMINISTRARE.includes(eu.rol);
  const poateOpera = OPERATIONAL.includes(eu.rol);

  useEffect(() => {
    api.calificariCareExpira(60).then(setExpira).catch(() => {});
  }, []);

  return (
    <div className="continut">
      <h1>{tr("Oameni și echipe")}</h1>
      <p className="subtitlu">
        {tr("Cine execută lucrarea, cu ce atestate, în ce echipă și pentru care client.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      {expira.length > 0 && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            <strong>{expira.filter((c) => c.expirata).length} {tr("atestate expirate")}</strong>
            {expira.some((c) => !c.expirata) &&
              ` și ${expira.filter((c) => !c.expirata).length} care expiră în 60 de zile`}.
            {" "}
            {expira.slice(0, 3).map((c) => `${c.tehnician.numeComplet} — ${c.calificare.denumire}`).join("; ")}
            {expira.length > 3 && ` și încă ${expira.length - 3}`}.
          </div>
        </div>
      )}

      <div className="taburi-sisteme">
        {SUBTABURI.map((s) => (
          <button key={s.cod} className={`tab tab-sistem ${subtab === s.cod ? "activ" : ""}`}
            onClick={() => setSubtab(s.cod)}>
            {tr(s.titlu)}
          </button>
        ))}
      </div>

      {subtab === "oameni" && <Tehnicieni poateAdministra={poateAdministra} poateOpera={poateOpera} setEroare={setEroare} />}
      {subtab === "echipe" && <EchipeLista poateAdministra={poateAdministra} setEroare={setEroare} />}
      {subtab === "alocari" && <Alocari poateOpera={poateOpera} setEroare={setEroare} />}
    </div>
  );
}

function Tehnicieni({ poateAdministra, poateOpera, setEroare }) {
  const [lista, setLista] = useState([]);
  const [cauta, setCauta] = useState("");
  const [inactivi, setInactivi] = useState(false);
  const [form, setForm] = useState(false);
  const [concediuPentru, setConcediuPentru] = useState(null);
  const [incarca, setIncarca] = useState(true);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setLista(await api.tehnicieni({ q: cauta || undefined, inactivi })); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [cauta, inactivi, setEroare]);

  useEffect(() => { reincarca(); }, [reincarca]);

  return (
    <>
      <div className="linie-actiune">
        <input className="camp camp-cautare" placeholder={tr("Caută după nume, matricol sau telefon…")}
          value={cauta} onChange={(e) => setCauta(e.target.value)} />
        {poateAdministra && (
          <button className="buton buton-actiune" onClick={() => setForm(true)}>{tr("tehnician nou")}</button>
        )}
      </div>
      <label className="comutator-dezactivate">
        <input type="checkbox" checked={inactivi} onChange={(e) => setInactivi(e.target.checked)} />
        {tr("arată și tehnicienii suspendați sau plecați")}
      </label>

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">
          {cauta ? tr("Niciun tehnician care să se potrivească.") : tr("Niciun tehnician încă.")}
        </div></div>
      ) : (
        <div className="panou">
          <div className="panou-antet"><h2>{tr("Tehnicieni")}</h2><span className="numar">{lista.length}</span></div>
          <div className="panou-corp">
            <table className="tabel">
              <thead>
                <tr><th>{tr("Nume")}</th><th>{tr("Matricol")}</th><th>{tr("Telefon")}</th><th>{tr("Zonă")}</th>
                    <th>{tr("Echipa principală")}</th><th>{tr("Atestate")}</th><th>{tr("Cont")}</th><th></th></tr>
              </thead>
              <tbody>
                {lista.map((t) => (
                  <tr key={t.id} className={t.statut !== "Activ" ? "tip-dezactivat" : ""}>
                    <td className="tip-denumire">
                      {t.numeComplet}
                      {t.statut !== "Activ" && <span className="eticheta eticheta-inactiv">{eticheta(STATUT_TEHNICIAN, t.statut)}</span>}
                    </td>
                    <td><code className="cod-mic">{t.matricol || "—"}</code></td>
                    <td>{t.telefon || "—"}</td>
                    <td>{t.zonaGeografica || "—"}</td>
                    <td>{t.echipaPrincipala?.denumire || <span className="fara-pret">{tr("fără")}</span>}</td>
                    <td>
                      {t.calificari.length === 0 ? <span className="fara-pret">{tr("niciunul")}</span> :
                        t.calificari.map((c) => (
                          <span key={c.id} className={c.expirata ? "eticheta eticheta-inactiv" : "marcaj"}
                            title={c.expiraLa ? tr("expiră {0}", data(c.expiraLa)) : tr("fără expirare")}>
                            {c.calificare.cod}
                          </span>
                        ))}
                    </td>
                    <td>
                      {t.areCont
                        ? <span className="eticheta eticheta-activ">{tr("are cont")}</span>
                        : <span className="fara-pret">{tr("fără cont")}</span>}
                    </td>
                    <td className="celula-actiuni">
                      {poateOpera && (
                        <button className="buton-mic" onClick={() => setConcediuPentru(t)}>{tr("indisponibilitate")}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="nota-cautare">
              {tr("Fișa există și fără cont de aplicație — poți planifica un colaborator care nu deschide niciodată programul. Când primește cont, se leagă de fișa existentă.")}
            </p>
          </div>
        </div>
      )}

      {form && (
        <FormTehnician inchide={() => setForm(false)}
          gata={async () => { setForm(false); await reincarca(); }} setEroare={setEroare} />
      )}
      {concediuPentru && (
        <FormIndisponibilitate tehnician={concediuPentru} inchide={() => setConcediuPentru(null)}
          gata={async () => { setConcediuPentru(null); await reincarca(); }} setEroare={setEroare} />
      )}
    </>
  );
}

function FormTehnician({ inchide, gata, setEroare }) {
  const [d, setD] = useState({ numeComplet: "", matricol: "", telefon: "", email: "", zonaGeografica: "" });
  const [salvez, setSalvez] = useState(false);
  const [eroare, setLocal] = useState("");

  const salveaza = async () => {
    setSalvez(true); setLocal("");
    try {
      await api.creeazaTehnician(Object.fromEntries(Object.entries(d).filter(([, v]) => String(v).trim())));
      await gata();
    } catch (e) { setLocal(mesajEroare(e)); setSalvez(false); }
  };

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Tehnician nou")}</h2>
        {eroare && <div className="eroare">{eroare}</div>}
        <label>{tr("Nume complet")}<input className="camp" value={d.numeComplet} autoFocus
          onChange={(e) => setD({ ...d, numeComplet: e.target.value })} /></label>
        <div className="formular-linie">
          <label>{tr("Matricol")}<input className="camp" value={d.matricol}
            onChange={(e) => setD({ ...d, matricol: e.target.value })} /></label>
          <label>{tr("Telefon")}<input className="camp" value={d.telefon}
            onChange={(e) => setD({ ...d, telefon: e.target.value })} /></label>
        </div>
        <div className="formular-linie">
          <label>{tr("E-mail")}<input className="camp" value={d.email}
            onChange={(e) => setD({ ...d, email: e.target.value })} /></label>
          <label>{tr("Zona în care lucrează")}<input className="camp" placeholder={tr("Ploiești")} value={d.zonaGeografica}
            onChange={(e) => setD({ ...d, zonaGeografica: e.target.value })} /></label>
        </div>
        <p className="nota-cautare">
          {tr("Contul de aplicație se leagă separat, din „Utilizatori\" — fișa poate exista și fără el.")}
        </p>
        <div className="modal-actiuni">
          <button className="buton buton-discret" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton buton-adauga" onClick={salveaza}
            disabled={d.numeComplet.trim().length < 3 || salvez}>
            {salvez ? tr("se salvează…") : tr("salvează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormIndisponibilitate({ tehnician, inchide, gata, setEroare }) {
  const [lista, setLista] = useState([]);
  const [d, setD] = useState({ tip: "Concediu", deLa: azi(), panaLa: azi(), motiv: "" });
  const [eroare, setLocal] = useState("");
  const [salvez, setSalvez] = useState(false);

  const reincarca = useCallback(async () => {
    try { setLista(await api.indisponibilitati(tehnician.id)); }
    catch (e) { setLocal(mesajEroare(e)); }
  }, [tehnician.id]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const salveaza = async () => {
    setSalvez(true); setLocal("");
    try {
      await api.adaugaIndisponibilitate(tehnician.id, {
        tip: d.tip,
        deLa: new Date(d.deLa).toISOString(),
        panaLa: new Date(d.panaLa).toISOString(),
        ...(d.motiv.trim() ? { motiv: d.motiv.trim() } : {}),
      });
      await reincarca();
      setD({ ...d, motiv: "" });
    } catch (e) { setLocal(mesajEroare(e)); }
    finally { setSalvez(false); }
  };

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tehnician.numeComplet}</h2>
        <p className="subtitlu">
          {tr("Perioadele în care nu e disponibil. Intră în propunerea automată de alocare.")}
        </p>
        {eroare && <div className="eroare">{eroare}</div>}

        {lista.length > 0 && (
          <table className="tabel">
            <thead><tr><th>{tr("Tip")}</th><th>{tr("De la")}</th><th>{tr("Până la")}</th><th>{tr("Motiv")}</th><th></th></tr></thead>
            <tbody>
              {lista.map((i) => (
                <tr key={i.id}>
                  <td>{tr(TIPURI_INDISPONIBILITATE.find((t) => t.cod === i.tip)?.titlu) ?? i.tip}</td>
                  <td>{data(i.deLa)}</td><td>{data(i.panaLa)}</td>
                  <td>{i.motiv || "—"}</td>
                  <td className="celula-actiuni">
                    <button className="buton-mic" onClick={async () => {
                      try { await api.stergeIndisponibilitate(i.id); await reincarca(); }
                      catch (e) { setLocal(mesajEroare(e)); }
                    }}>{tr("șterge")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="formular-linie">
          <label>{tr("Tip")}
            <select className="camp" value={d.tip} onChange={(e) => setD({ ...d, tip: e.target.value })}>
              {TIPURI_INDISPONIBILITATE.map((t) => <option key={t.cod} value={t.cod}>{tr(t.titlu)}</option>)}
            </select>
          </label>
          <label>{tr("De la")}<input className="camp" type="date" value={d.deLa}
            onChange={(e) => setD({ ...d, deLa: e.target.value })} /></label>
          <label>{tr("Până la")}<input className="camp" type="date" value={d.panaLa}
            onChange={(e) => setD({ ...d, panaLa: e.target.value })} /></label>
        </div>
        <label>{tr("Motiv")}<input className="camp" value={d.motiv}
          onChange={(e) => setD({ ...d, motiv: e.target.value })} /></label>

        <div className="modal-actiuni">
          <button className="buton buton-discret" onClick={async () => { await gata(); }}>{tr("închide")}</button>
          <button className="buton buton-adauga" onClick={salveaza} disabled={salvez}>
            {salvez ? tr("se adaugă…") : tr("adaugă perioada")}
          </button>
        </div>
      </div>
    </div>
  );
}

function EchipeLista({ poateAdministra, setEroare }) {
  const [lista, setLista] = useState([]);
  const [oameni, setOameni] = useState([]);
  const [inactive, setInactive] = useState(false);
  const [form, setForm] = useState(false);
  const [adaugLa, setAdaugLa] = useState(null);
  const [membruNou, setMembruNou] = useState({ tehnicianId: "", principal: false });

  const reincarca = useCallback(async () => {
    try {
      setLista(await api.echipe(inactive));
      setOameni(await api.tehnicieni());
    } catch (e) { setEroare(mesajEroare(e)); }
  }, [inactive, setEroare]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const adauga = async (echipaId) => {
    try {
      await api.adaugaMembru(echipaId, membruNou);
      setAdaugLa(null); setMembruNou({ tehnicianId: "", principal: false });
      await reincarca();
    } catch (e) { setEroare(mesajEroare(e)); }
  };

  return (
    <>
      <div className="linie-actiune" style={{ alignItems: "center" }}>
        <label className="comutator-dezactivate">
          <input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} />
          {tr("arată și echipele dezactivate")}
        </label>
        {poateAdministra && (
          <button className="buton buton-actiune" onClick={() => setForm(true)}>{tr("echipă nouă")}</button>
        )}
      </div>

      {lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">{tr("Nicio echipă încă.")}</div></div>
      ) : lista.map((e) => (
        <div className="panou" key={e.id}>
          <div className="panou-antet">
            <h2>{tr(e.denumire)}</h2>
            {e.activ === false && <span className="eticheta eticheta-inactiv">{tr("dezactivată")}</span>}
            {e.zonaGeografica && <span className="numar">{e.zonaGeografica}</span>}
            <span className="numar">{e.membri.length === 1 ? tr("un membru") : `${e.membri.length} membri`}</span>
            {poateAdministra && (
              <>
                <button className="buton-mic" onClick={() => setAdaugLa(e.id)}>{tr("adaugă membru")}</button>
                <button className="buton-mic" onClick={async () => {
                  try { await api.comutaEchipa(e.id, e.activ === false); await reincarca(); }
                  catch (er) { setEroare(mesajEroare(er)); }
                }}>{e.activ === false ? tr("reactivează") : tr("dezactivează")}</button>
              </>
            )}
          </div>
          <div className="panou-corp">
            <div className="rezultat-context">
              {tr("Coordonator:")} {e.coordonator
                ? `${e.coordonator.numeComplet}${e.coordonator.telefon ? " · " + e.coordonator.telefon : ""}`
                : tr("nedesemnat")}
            </div>
            {e.membri.length === 0 ? (
              <div className="gol">{tr("Niciun membru.")}</div>
            ) : (
              <table className="tabel">
                <thead><tr><th>{tr("Membru")}</th><th>{tr("Zonă")}</th><th>{tr("Rol în echipă")}</th><th></th></tr></thead>
                <tbody>
                  {e.membri.map((m) => (
                    <tr key={m.tehnicianId}>
                      <td className="tip-denumire">{m.tehnician.numeComplet}</td>
                      <td>{m.tehnician.zonaGeografica || "—"}</td>
                      <td>
                        {m.principal
                          ? <span className="pastila pastila-soft">{tr("echipa lui principală")}</span>
                          : <span className="fara-pret">{tr("principala lui e altă echipă")}</span>}
                      </td>
                      <td className="celula-actiuni">
                        {poateAdministra && (
                          <button className="buton-mic" onClick={async () => {
                            try { await api.scoateMembru(e.id, m.tehnicianId); await reincarca(); }
                            catch (er) { setEroare(mesajEroare(er)); }
                          }}>{tr("scoate")}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {adaugLa === e.id && (
              <div className="linie-actiune">
                <label>{tr("Tehnician")}
                  <select className="camp" value={membruNou.tehnicianId}
                    onChange={(ev) => setMembruNou({ ...membruNou, tehnicianId: ev.target.value })}>
                    <option value="">{tr("— alege —")}</option>
                    {oameni.filter((o) => !e.membri.some((m) => m.tehnicianId === o.id))
                      .map((o) => <option key={o.id} value={o.id}>{o.numeComplet}</option>)}
                  </select>
                </label>
                <label className="linie-bifa">
                  <input type="checkbox" checked={membruNou.principal}
                    onChange={(ev) => setMembruNou({ ...membruNou, principal: ev.target.checked })} />
                  {tr("e echipa lui principală")}
                </label>
                <button className="buton buton-actiune" disabled={!membruNou.tehnicianId}
                  onClick={() => adauga(e.id)}>{tr("adaugă")}</button>
                <button className="buton buton-discret buton-actiune" onClick={() => setAdaugLa(null)}>{tr("renunță")}</button>
              </div>
            )}
          </div>
        </div>
      ))}

      <p className="nota-cautare">
        {tr("Un tehnician poate fi în mai multe echipe, dar are exact una principală — pe aceea o folosește propunerea automată. Bifând-o aici, cea veche se retrogradează singură.")}
      </p>

      {form && (
        <FormEchipa oameni={oameni} inchide={() => setForm(false)}
          gata={async () => { setForm(false); await reincarca(); }} />
      )}
    </>
  );
}

function FormEchipa({ oameni, inchide, gata }) {
  const [d, setD] = useState({ denumire: "", coordonatorId: "", zonaGeografica: "" });
  const [eroare, setEroare] = useState("");
  const [salvez, setSalvez] = useState(false);

  const salveaza = async () => {
    setSalvez(true); setEroare("");
    try {
      await api.creeazaEchipa({
        denumire: d.denumire.trim(),
        ...(d.coordonatorId ? { coordonatorId: d.coordonatorId } : {}),
        ...(d.zonaGeografica.trim() ? { zonaGeografica: d.zonaGeografica.trim() } : {}),
      });
      await gata();
    } catch (e) { setEroare(mesajEroare(e)); setSalvez(false); }
  };

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{tr("Echipă nouă")}</h2>
        {eroare && <div className="eroare">{eroare}</div>}
        <label>{tr("Denumire")}<input className="camp" value={d.denumire} autoFocus
          onChange={(e) => setD({ ...d, denumire: e.target.value })} /></label>
        <div className="formular-linie">
          <label>{tr("Coordonator")}
            <select className="camp" value={d.coordonatorId}
              onChange={(e) => setD({ ...d, coordonatorId: e.target.value })}>
              <option value="">{tr("— nedesemnat —")}</option>
              {oameni.map((o) => <option key={o.id} value={o.id}>{o.numeComplet}</option>)}
            </select>
          </label>
          <label>{tr("Zonă")}<input className="camp" value={d.zonaGeografica}
            onChange={(e) => setD({ ...d, zonaGeografica: e.target.value })} /></label>
        </div>
        <p className="nota-cautare">{tr("Coordonatorul e un tehnician — merge și el pe teren.")}</p>
        <div className="modal-actiuni">
          <button className="buton buton-discret" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton buton-adauga" onClick={salveaza}
            disabled={d.denumire.trim().length < 2 || salvez}>
            {salvez ? tr("se salvează…") : tr("salvează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Alocari({ poateOpera, setEroare }) {
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [lista, setLista] = useState([]);
  const [oameni, setOameni] = useState([]);
  const [echipe, setEchipe] = useState([]);
  const [propuneri, setPropuneri] = useState(null);
  const [nou, setNou] = useState({ tinta: "", rol: "Principal" });

  useEffect(() => {
    api.clienti().then(setClienti).catch((e) => setEroare(mesajEroare(e)));
    api.tehnicieni().then(setOameni).catch(() => {});
    api.echipe().then(setEchipe).catch(() => {});
  }, [setEroare]);

  const reincarca = useCallback(async () => {
    if (!clientId) { setLista([]); setPropuneri(null); return; }
    try {
      setLista(await api.alocariClient(clientId));
      setPropuneri(await api.propuneTehnicieni({ clientId }));
    } catch (e) { setEroare(mesajEroare(e)); }
  }, [clientId, setEroare]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const active = useMemo(() => lista.filter((a) => !a.valabilPanaLa), [lista]);
  const incheiate = useMemo(() => lista.filter((a) => a.valabilPanaLa), [lista]);

  const aloca = async () => {
    const [fel, id] = nou.tinta.split(":");
    try {
      await api.aloca(clientId, {
        ...(fel === "t" ? { tehnicianId: id } : { echipaId: id }),
        rol: nou.rol,
      });
      setNou({ tinta: "", rol: "Principal" });
      await reincarca();
    } catch (e) { setEroare(mesajEroare(e)); }
  };

  return (
    <>
      <div className="panou">
        <div className="panou-corp">
          <label>{tr("Client")}
            <select className="camp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">{tr("— alege clientul —")}</option>
              {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
            </select>
          </label>
        </div>
      </div>

      {!clientId ? (
        <div className="panou"><div className="panou-corp gol">
          {tr("Alege un client ca să vezi cine răspunde de el.")}
        </div></div>
      ) : (
        <>
          <div className="panou">
            <div className="panou-antet"><h2>{tr("Cine răspunde")}</h2><span className="numar">{active.length}</span></div>
            <div className="panou-corp">
              {active.length === 0 ? (
                <div className="gol">{tr("Nimeni alocat. Tichetele lui ajung în coada generală.")}</div>
              ) : (
                <table className="tabel">
                  <thead><tr><th>{tr("Rol")}</th><th>{tr("Cine")}</th><th>{tr("Contact")}</th><th>{tr("De la")}</th><th></th></tr></thead>
                  <tbody>
                    {active.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <span className={a.rol === "Principal" ? "pastila pastila-soft" : "marcaj"}>
                            {a.rol === "Principal" ? tr("principal") : "backup"}
                          </span>
                        </td>
                        <td className="tip-denumire">
                          {a.tehnician?.numeComplet ?? a.echipa?.denumire}
                          {a.echipa && <span className="marcaj">{tr("echipă")}</span>}
                        </td>
                        <td>
                          {a.tehnician?.telefon
                            ?? (a.echipa?.coordonator
                              ? `${a.echipa.coordonator.numeComplet}${a.echipa.coordonator.telefon ? " · " + a.echipa.coordonator.telefon : ""}`
                              : "—")}
                        </td>
                        <td>{data(a.valabilDeLa)}</td>
                        <td className="celula-actiuni">
                          {poateOpera && (
                            <button className="buton-mic" onClick={async () => {
                              try { await api.incheieAlocare(a.id); await reincarca(); }
                              catch (e) { setEroare(mesajEroare(e)); }
                            }}>{tr("încheie")}</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {poateOpera && (
                <div className="linie-actiune">
                  <label>{tr("Alocă")}
                    <select className="camp" value={nou.tinta}
                      onChange={(e) => setNou({ ...nou, tinta: e.target.value })}>
                      <option value="">{tr("— alege —")}</option>
                      <optgroup label="Echipe">
                        {echipe.map((e) => <option key={e.id} value={`e:${e.id}`}>{tr(e.denumire)}</option>)}
                      </optgroup>
                      <optgroup label="Tehnicieni">
                        {oameni.map((o) => <option key={o.id} value={`t:${o.id}`}>{o.numeComplet}</option>)}
                      </optgroup>
                    </select>
                  </label>
                  <label>{tr("Ca")}
                    <select className="camp" value={nou.rol} onChange={(e) => setNou({ ...nou, rol: e.target.value })}>
                      <option value="Principal">{tr("principal")}</option>
                      <option value="Backup">{tr("backup")}</option>
                    </select>
                  </label>
                  <button className="buton buton-actiune" disabled={!nou.tinta} onClick={aloca}>{tr("alocă")}</button>
                </div>
              )}
              <p className="nota-cautare">
                {tr("Un client are un singur responsabil principal. Cel vechi se închide automat, nu se șterge — rămâne scris cine răspundea atunci.")}
              </p>
            </div>
          </div>

          {propuneri && (
            <div className="panou">
              <div className="panou-antet">
                <h2>{tr("Cine ar putea merge")}</h2>
                <span className="numar">{tr("în ordinea potrivirii")}</span>
              </div>
              <div className="panou-corp">
                <table className="tabel">
                  <thead><tr><th>{tr("Tehnician")}</th><th>{tr("Echipa principală")}</th><th>{tr("Zonă")}</th><th>{tr("De ce")}</th></tr></thead>
                  <tbody>
                    {propuneri.propuneri.map((p) => (
                      <tr key={p.tehnicianId} className={p.potrivit ? "" : "tip-dezactivat"}>
                        <td className="tip-denumire">
                          {p.numeComplet}
                          {!p.potrivit && <span className="eticheta eticheta-inactiv">{tr("nu acum")}</span>}
                        </td>
                        <td>{p.echipaPrincipala?.denumire || "—"}</td>
                        <td>{p.zonaGeografica || "—"}</td>
                        <td>{p.motive.join("; ") || <span className="fara-pret">{tr("nimic care să-l lege de client")}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="nota-cautare">
                  {tr("Nimeni nu e ascuns din listă: cine nu se potrivește acum apare cu motivul scris. Un dispecer poate ști ceva ce aplicația nu știe — că un concediu tocmai s-a anulat.")}
                </p>
              </div>
            </div>
          )}

          {incheiate.length > 0 && (
            <div className="panou">
              <div className="panou-antet"><h2>{tr("Alocări încheiate")}</h2><span className="numar">{incheiate.length}</span></div>
              <div className="panou-corp">
                <table className="tabel">
                  <thead><tr><th>{tr("Rol")}</th><th>{tr("Cine")}</th><th>{tr("Perioadă")}</th></tr></thead>
                  <tbody>
                    {incheiate.map((a) => (
                      <tr key={a.id} className="tip-dezactivat">
                        <td>{a.rol === "Principal" ? tr("principal") : "backup"}</td>
                        <td>{a.tehnician?.numeComplet ?? a.echipa?.denumire}</td>
                        <td>{data(a.valabilDeLa)} → {data(a.valabilPanaLa)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
